/* eslint-disable valid-typeof */
import React, { useState, useCallback, useMemo, Suspense, useRef, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import {
    VideoThumbnailWrapper,
    ThumbnailCom,
    ThumbnailImage,
    VideoPlayerWrapper
} from './VideoPlayer.style';
import { initConfig, mediaConfig, controlsConfig, advertisingConfig } from '@gameday/utils/config/videoPlayerConfig';
import MetaInfo from './MetaInfo';
import lockViewAction from '@gameday/app/lib/store/actions/lockViewAction';
import { useDispatch, useSelector } from 'react-redux';
import { resourcePath } from '@gameday/utils/resource-path';
import { getVideoPlayerState } from './store/selectors/getVideoPlayerInfo';
import useAds from '@gameday/utils/hooks/useAds';
import normalizationHelper, { formatInstructionSet, RATIO } from './utils/normalizationHelper';
import { setPlayerConfig } from '@gameday/utils/config/tracking-video';
import debounce from 'lodash/debounce';
import { getAppVideoSettingPermission } from '@gameday/utils/selectors/getAppVideoSettingPermission';
import { getSafeLanguagePath } from '@gameday/utils/languageHelpers';
import handleCaptionMessage from './utils/handleCaptionMessage';

import DfpRequestBuilder from './utils/DfpRequestBuilder';
import fetchAdBids from './utils/fetchAdBids';
import { buildAdParams } from './utils/buildAdParams';
import { bidProcessors } from './utils/bidProcessors';

const UNDEF = 'undefined';
const FREE = ['free'];
const ENABLED = 'enabled';
const DISABLED = 'disabled';
const SHOWING = 'showing';
const HIDDEN = 'hidden';

const noOp = () => {};
const MediaPlaybackWrapper = React.lazy(() => import(/* webpackChunkName: "mediaPlayback" */ '@mlb/react-patterns/v4/components/MediaPlayback/mediaPlaybackWrapper'));
const formatInsStr = '{formatInstructions}';
const defaultPrerollType = 'highlight';

const customAdRequestBuilder = async (config, mediaItem, cuePointVO, options) => {
    const { adsGameInfo, prerollType, entitlement = FREE, mbox, id, teamNames, season, enabledAdSources, publicaParams } = options;
    const { gamePk, teams, inning, status, env, gameState, prerollIu, pp, cmsid } = adsGameInfo;

    const clonedConfig = { ...config };
    clonedConfig.iu = prerollIu[prerollType];
    clonedConfig.pp = pp;
    clonedConfig.description_url = window.location.href;
    clonedConfig.current_url = window.location.href;
    clonedConfig.vid = id;
    clonedConfig.cust_params = {
        gid: gamePk,
        team: teams,
        env,
        entitlement
    };
    if (gameState !== 'Final') {
        clonedConfig.cust_params.inning = inning;
        clonedConfig.cust_params.status = status;
    }
    if (cmsid) {
        clonedConfig.cmsid = cmsid;
    }
    clonedConfig.correlator = new Date().getTime();
    if (mbox?.gamedayAdFrequency) { // specific about the key
        clonedConfig.cust_params.gamedayAdFrequency = mbox.gamedayAdFrequency;
    }

    const adFetchTasks = enabledAdSources?.reduce((acc, source) => {
        const params = buildAdParams({
            config: clonedConfig,
            media: mediaItem,
            teamNames,
            prerollType,
            source,
            env,
            season,
            enabledAdSources,
            publicaParams
        });
        acc.promises.push(fetchAdBids(params, source));
        acc.sources.push(source);
        return acc;
    }, { promises: [], sources: [] });

    // Merge processed bids into custom_params
    const bidResponses = await Promise.allSettled(adFetchTasks.promises);

    // Process all bid responses using the bidProcessors registry
    const processedBids = bidResponses?.reduce((acc, result, idx) => {
        if (result.status === 'fulfilled') {
            const source = adFetchTasks.sources[idx];
            const processor = bidProcessors[source];
            if (processor) {
                Object.assign(acc, processor(result.value));
            } else {
                console.warn(`No processor found for source: ${source}`);
            }
        }
        return acc;
    }, {});

    clonedConfig.cust_params = {
        ...clonedConfig.cust_params,
        ...processedBids
    };

    return DfpRequestBuilder.build(clonedConfig, mediaItem, cuePointVO);
};

const VideoPlayer = (props) => {
    const {
        id,
        title = '',
        description = '',
        duration = '',
        showMetaInfo = false,
        playbackContainerId,
        showTitle,
        templateUrl = '',
        slug = '',
        width,
        handlers = {},
        autoPlay = false,
        mediaConfigOverride,
        mute,
        playbacks = [],
        prerollType = defaultPrerollType,
        playerContext,
        onMetaDataLoaded = noOp,
        mediaItemList,
        setPlayerWrapper = noOp,
        playerConfigOverride,
        captions,
        countdown = 3
    } = props;

    const { appMute, appCaptions } = useSelector(getAppVideoSettingPermission);
    const playerContextText = playerContext ? 'Gameday: ' + playerContext : playerContext;
    const { language, mbox, entitlement, teamNames, season, enabledAdSources, videoConfig } = useSelector(getVideoPlayerState);
    const {
        adComplete = noOp,
        mediaComplete = noOp,
        mediaReplay = noOp,
        onClick = noOp,
        mediaStart = noOp,
        setDynamicTrackingObj = noOp,
        showClickToPlay = noOp,
        embedPlayerStart = noOp,
        onClickToPlay = noOp
    } = handlers; // TODO: break up -> subscriptions vs non
    const AdsInfo = useAds();
    const adsGameInfo = AdsInfo.getKeyVals().data || {};
    const { gamePk, prerollIu, pp } = adsGameInfo;
    const { publica } = videoConfig;
    const divRef = useRef(null);
    const playerWrapper = React.useRef(null);

    const player = React.useRef(null);
    const isPlayerPaused = React.useRef(false);
    const isLockView = React.useRef(false);
    const firstVideoStarted = useRef(false);
    const captionSettingRef = useRef(undefined);
    const videoCaptionSubscription = React.useRef();
    const byMediaCompleteRef = useRef(false);

    const [metaVisibility, setMetaVisibility] = useState(false);
    const [playerReady, setPlayerReady] = useState(false);
    const [videoInitialized, setVideoInitialized] = useState(false);
    const dispatch = useDispatch();
    const enabledPreroll = !!(prerollIu && pp);
    const dataAttr = {
        'data-id': slug,
        'data-url': resourcePath.get('playVideoLink', {
            languagePath: getSafeLanguagePath(language),
            cid: slug
        }),
        'data-title': title
    };

    useEffect(() => {
        if (autoPlay) {
            setVideoInitialized(true);
        }
    }, [autoPlay]);

    const srcSet = useMemo(() => {
        return {
            '1x': templateUrl?.replace(formatInsStr, formatInstructionSet['1x']),
            '2x': templateUrl?.replace(formatInsStr, formatInstructionSet['2x']),
            '3x': templateUrl?.replace(formatInsStr, formatInstructionSet['3x'])
        };
    }, [templateUrl]);

    const mergedMediaConfig = useMemo(() => {
        if (videoInitialized && enabledPreroll) {
            return { ...mediaConfig, ...advertisingConfig };
        }
        return mediaConfig;
    }, [
        advertisingConfig,
        mediaConfig,
        videoInitialized,
        enabledPreroll
    ]);

    const setLocked = (fsMode, vid) => {
        if (fsMode !== isLockView.current) {
            isLockView.current = Boolean(fsMode);
            dispatch(lockViewAction({ lockView: isLockView.current, videoPlaying: vid }));
        }
    };

    const debouncedUnlock = useCallback(
        debounce((fsMode, vid) => {
            // add another FS check just in case the user spams fullscreen within 500ms.
            const hasFS = document?.fullscreenElement !== null || player?.current?.player?.videoEl?.webkitDisplayingFullscreen;
            if (!hasFS) {
                setLocked(fsMode, vid);
            }
        }, 500),
        []
    );

    const handleObservable = useCallback((observationCenter) => {
        observationCenter.stateSubject.subscribe((data) => {
            const { stateModel } = data;
            if (data.type && data.type !== 'empty' && stateModel) {
                isPlayerPaused.current = Boolean(stateModel.paused);
                const vid = playerWrapper?.current?.playerId;
                if (typeof document?.fullscreenElement !== UNDEF) {
                    // everything but desktop safari
                    const hasFullscreenElement = document?.fullscreenElement !== null;
                    if (hasFullscreenElement) {
                        const fsMode = hasFullscreenElement;
                        setLocked(fsMode, vid);
                    } else {
                        // mobile safari
                        const fsMode = player?.current?.player?.videoEl?.webkitDisplayingFullscreen; // mobile safari
                        if (fsMode) { // if mobile safari, we may be locking or unlocking.
                            setLocked(fsMode, vid);
                        } else { // otherwise, delayed unlock.
                            debouncedUnlock(fsMode, vid);
                        }
                    }
                }
            }
        });

        observationCenter.embedSubject.subscribe((message) => {
            const { type = '' } = message;

            if (type === 'embedPlayerStart') {
                setMetaVisibility(false);
                embedPlayerStart();
            }

            if (type === 'showClickToPlay') {
                setMetaVisibility(true);
                showClickToPlay();
            }

            if (type === 'clickToPlay' && message.wasClickToPlay === true) {
                onClickToPlay();
            }

            if (type === 'mediaStart') {
                setMetaVisibility(false);
                mediaStart();
            }

            if (mediaComplete && type === 'mediaComplete') {
                mediaComplete();
                const mediaPlayerInstance = player?.current;
                const videoPlayer = mediaPlayerInstance?.player;
                const mode = videoPlayer?.videoEl?.textTracks?.[0]?.mode;
                const activeCues = videoPlayer?.videoEl?.textTracks?.[0]?.activeCues;

                if (activeCues) {
                    captionSettingRef.current = mode === SHOWING ? ENABLED : DISABLED;
                    byMediaCompleteRef.current = true;
                }
            }

            if (mediaReplay && type === 'mediaReplay') {
                mediaReplay();
            }
        });

        observationCenter.playerInitSubject.subscribe((message) => {
            if (message.type === 'MEDIA_ITEM_SET') {
                onMetaDataLoaded(message?.data?.metaData);
            }
        });

        observationCenter.pluginSubject.subscribe((message) => {
            const { type = '' } = message;
            if (type === 'adLoaded') {
                setMetaVisibility(false);
            }
            if (adComplete && type === 'adComplete') {
                adComplete();
            }
        });

        videoCaptionSubscription.current = observationCenter.playerSubject.subscribe(
            handleCaptionMessage({
                player,
                firstVideoStarted,
                byMediaCompleteRef,
                captionSettingRef,
                captions,
                appCaptions,
                ENABLED,
                DISABLED,
                SHOWING,
                HIDDEN,
                videoCaptionSubscription
            })
        );
    }, [
        isPlayerPaused,
        isLockView,
        mediaComplete,
        mediaReplay,
        adComplete,
        captions,
        appCaptions,
        mediaStart,
        onMetaDataLoaded,
        onClickToPlay,
        player,
        showClickToPlay,
        embedPlayerStart
    ]);

    const handleMediaPlayerReady = useCallback((mediaPlayer) => {
        if (mediaPlayer) {
            setPlayerReady(true);
            player.current = mediaPlayer;

            if (mediaPlayer && setDynamicTrackingObj !== noOp) {
                const originalTrack = mediaPlayer?.pluginManager?.pluginsMap?.OmniturePlugin?.wiretapVideo?.setTrackingProps;
                if (originalTrack) {
                    mediaPlayer.pluginManager.pluginsMap.OmniturePlugin.wiretapVideo.setTrackingProps = function (trackObj, eventData) {
                        return originalTrack.call(this, (setDynamicTrackingObj(trackObj) ?? trackObj), eventData);
                    };
                }
            }
        }
    }, [setPlayerReady, player, setDynamicTrackingObj]);

    const handleEmbedWrapper = useCallback((embedWrapper) => {
        playerWrapper.current = embedWrapper;
        setPlayerWrapper(embedWrapper);
    }, [playerWrapper]);

    const onClickCallBack = useCallback((e) => {
        e?.stopPropagation();
        e?.preventDefault();

        setVideoInitialized(true);

        onClick(e);
    }, [onClick, playerWrapper]);
    const mediaConfigInfo = mediaConfigOverride || mergedMediaConfig;
    const initConfigData = useMemo(() => {
        return enabledPreroll
            ? {
                ...initConfig,
                countdown,
                dynamicAdConfig: {
                    customAdRequestBuilder: (adConfig, clip, cuePointVO) => {
                        return customAdRequestBuilder(adConfig, clip, cuePointVO, {
                            adsGameInfo,
                            prerollType,
                            entitlement,
                            mbox: (prerollType === defaultPrerollType) ? mbox : null,
                            id,
                            teamNames,
                            season,
                            enabledAdSources,
                            publicaParams: publica
                        });
                    }
                }
            }
            : initConfig;
    }, [
        enabledPreroll,
        initConfig,
        mediaConfigInfo,
        adsGameInfo,
        prerollType,
        defaultPrerollType,
        entitlement,
        mbox,
        id,
        countdown,
    ]);

    const playerOptions = useMemo(() => {
        let setting = false;

        if (typeof mute !== UNDEF) {
            setting = mute;
        } else if (typeof appMute !== UNDEF) {
            setting = appMute;
        }
        return { mute: setting };
    }, [mute, appMute]);

    const normalizedMediaItem = useMemo(() => {
        if (videoInitialized && playbacks?.length > 0) {
            return normalizationHelper({ id, title, playbacks, srcSet, language, gamePk, description, duration });
        }

        return null;
    }, [videoInitialized, srcSet, id, title, playbacks, language, gamePk, description, duration]);

    const analyticsConfig = useMemo(() => {
        const temp = {
            omniture: {
                enabled: true,
                siteTraffic: 'mlb',
                section: 'Gameday',
                playerContext: playerContextText,
                bandwagonPlayerEvents: {
                    MEDIA_START: 'play',
                    MEDIA_COMPLETE: 'ended',
                    PLAYBACK_PERCENT_25: 'playbackPercent25',
                    PLAYBACK_PERCENT_50: 'playbackPercent50',
                    PLAYBACK_PERCENT_75: 'playbackPercent75'
                }
            }
        };

        const override = playerConfigOverride ? { ...playerConfigOverride } : {};
        temp.omniture.wiretapVideoJson = setPlayerConfig(override);

        return temp;
    }, [playerContext, id, setPlayerConfig, playerConfigOverride]);

    return (
        videoInitialized && normalizedMediaItem
            ? (
                <VideoPlayerWrapper
                    $width={width}
                >
                    <Suspense fallback={<></>}>
                        <MediaPlaybackWrapper
                            playbackContainerId={playbackContainerId}
                            handleObservable={handleObservable}
                            mediaConfig={mediaConfigInfo}
                            handleEmbedWrapper={handleEmbedWrapper}
                            handleMediaPlayerReady={handleMediaPlayerReady}
                            trackingConfig={analyticsConfig}
                            controlsConfig={controlsConfig}
                            autoPlay
                            initConfig={initConfigData}
                            playerOptions={playerOptions}
                            mediaItem={normalizedMediaItem}
                            mediaItemList={mediaItemList}
                        />
                    </Suspense>
                    {showMetaInfo && playerReady && (
                        <MetaInfo
                            showTitle={showTitle}
                            title={title}
                            duration={duration}
                            id={id}
                            metaVisibility={metaVisibility}
                        />
                    )}
                </VideoPlayerWrapper>
            )
            : (
                <VideoThumbnailWrapper
                    onClick={onClickCallBack}
                    ref={divRef}
                >
                    <ThumbnailCom
                        dataAttr={dataAttr}
                        target={'_blank'}
                        url={dataAttr['data-url']}
                        $width={width}
                        ariaLabel={title}
                    >
                        <ThumbnailImage
                            ratio={RATIO}
                            src={ srcSet['2x'] }
                            srcset={srcSet}
                            alt={ title }
                            width={width}
                        />
                        {showMetaInfo && (
                            <MetaInfo
                                title={title}
                                duration={duration}
                                metaVisibility={true}
                                showTitle={showTitle}
                            />
                        )}
                    </ThumbnailCom>
                </VideoThumbnailWrapper>
            )
    );
};

VideoPlayer.propTypes = {
    id: PropTypes.string,
    description: PropTypes.string,
    duration: PropTypes.string,
    title: PropTypes.string,
    playbackContainerId: PropTypes.string,
    trackingId: PropTypes.string,
    showMetaInfo: PropTypes.bool,
    showTitle: PropTypes.bool,
    slug: PropTypes.string,
    templateUrl: PropTypes.string,
    width: PropTypes.string,
    height: PropTypes.string,
    handlers: PropTypes.object,
    autoPlay: PropTypes.bool,
    mediaConfigOverride: PropTypes.object,
    playbacks: PropTypes.array,
    mute: PropTypes.bool,
    prerollType: PropTypes.string,
    playerContext: PropTypes.string,
    onMetaDataLoaded: PropTypes.func,
    playerConfigOverride: PropTypes.object,
    captions: PropTypes.bool,
    mediaItemList: PropTypes.array,
    setPlayerWrapper: PropTypes.func,
    countdown: PropTypes.number,
};

export default memo(VideoPlayer);
